import React from "react";
import { Button, TabsList, TabsTrigger } from "@racoon/ui";
import { useT, Namespace } from "@racoon/i18n";
import { DateValue } from "~/shared/lib/analytics-utils";
import { AnalyticsFilterBlock } from "./AnalyticsFilterBlock";
import { CategoryChips, CategoryKey } from "./CategoryChips";
import { REPORT_TABS, reportTab } from "./report-tabs";
import { useReportFilterParams } from "../model/use-report-filters";

interface Props {
  activeTab: string;
  activeReportTab: string;
  category: CategoryKey;
  startDate: DateValue;
  endDate: DateValue;
  onReportTabChange: (tab: string) => void;
  onCategoryChange: (category: CategoryKey) => void;
  onStartDateChange: (date: Date | undefined) => void;
  onEndDateChange: (date: Date | undefined) => void;
  onDownloadExcel?: () => Promise<void> | void;
  onDownloadPdf?: () => Promise<void> | void;
  onPrint?: () => void;
}

// Original chip shape; hover/transition behavior matches the FilterBlock state buttons
// (Автопарк / Смены / Ремонт): inactive hover -> bg-accent, active hover -> opacity-90.
// Bordered tabs, not the segmented control: unstyled, so the default's look doesn't leak in.
const tabsTriggerClasses =
  "h-8 flex items-center px-3 text-sm font-medium border border-border-primary rounded-lg bg-white text-text-primary transition-colors " +
  "hover:bg-accent data-[state=active]:bg-brand-blue data-[state=active]:text-white " +
  "data-[state=active]:hover:bg-brand-blue data-[state=active]:hover:opacity-90 focus-visible:ring-brand-blue";

export const AnalyticsFilters: React.FC<Props> = ({
  activeTab,
  activeReportTab,
  category,
  startDate,
  endDate,
  onReportTabChange,
  onCategoryChange,
  onStartDateChange,
  onEndDateChange,
  onDownloadExcel,
  onDownloadPdf,
  onPrint,
}) => {
  const t = useT();
  const { filterParams, onFilterParamsChange, onFilterSheetOpenChange } = useReportFilterParams(
    reportTab(activeReportTab).filters,
  );

  return (
    <>
      {/* Row 1: view tabs. */}
      <div className="mb-3 flex items-start gap-3">
        <TabsList variant="unstyled" className="flex h-8 justify-start gap-x-2">
          <TabsTrigger
            id="analytics-tab-charts-button"
            value="charts"
            variant="unstyled"
            className={tabsTriggerClasses}
          >
            {t(Namespace.Analytics, "tabs.charts")}
          </TabsTrigger>
          <TabsTrigger
            id="analytics-tab-reports-button"
            value="reports"
            variant="unstyled"
            className={tabsTriggerClasses}
          >
            {t(Namespace.Analytics, "tabs.reports")}
          </TabsTrigger>
        </TabsList>
      </div>

      {/* Row 2: date range on the left — same placement on both tabs; the charts tab
          carries the job-type filter on the right. */}
      <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <AnalyticsFilterBlock
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={onStartDateChange}
          onEndDateChange={onEndDateChange}
          onDownloadExcel={onDownloadExcel}
          onDownloadPdf={onDownloadPdf}
          onPrint={onPrint}
          showActionButtons={activeTab === "reports"}
          filterParams={activeTab === "reports" ? filterParams : undefined}
          onFilterParamsChange={onFilterParamsChange}
          onFilterSheetOpenChange={onFilterSheetOpenChange}
        />
        {activeTab === "charts" && <CategoryChips value={category} onChange={onCategoryChange} />}
      </div>

      {/* Sub-tabs for reports */}
      {activeTab === "reports" && (
        <div className="mb-2 flex flex-row items-center gap-2">
          <div className="flex flex-row items-center overflow-x-auto pb-1">
            {REPORT_TABS.map((tab) => (
              <Button
                key={tab.id}
                id={`analytics-report-${tab.id}-button`}
                variant={activeReportTab === tab.id ? "default" : "outline"}
                className="mr-2 h-[32px] shrink-0 border border-gray-200 rounded-lg px-3 py-2 font-light shadow-none"
                onClick={() => onReportTabChange(tab.id)}
              >
                {t(Namespace.Analytics, tab.label)}
              </Button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};
